import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Light / dark theme, chosen per person and remembered in this browser.
 *
 * Three states: 'light' (the default -- dark is opt-in), 'dark', and
 * 'system' for anyone who has that stored. The
 * class on <html> is what Tailwind's darkMode: 'class' reads, and it is also
 * set by the inline script in index.html before first paint so the page never
 * flashes the wrong theme on load.
 */

export const THEME_KEY = 'prohr-theme';
const ThemeContext = createContext(null);

function systemPrefersDark() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function readStored() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    // Default is light, deliberately: dark is opt-in. Following the OS would
    // hand a dark ProHR to anyone whose laptop is set dark, without them
    // choosing it. 'system' is still honoured for anyone who has it stored.
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    // Private windows and locked-down browsers throw on access rather than
    // returning null, so every read and write here is guarded.
    return 'light';
  }
}

function applyTheme(theme) {
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
  // Keep the browser chrome (mobile address bar) in step with the page.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#0c1021' : '#f5f6f8');
  return dark;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);
  const [isDark, setIsDark] = useState(() => applyTheme(readStored()));

  useEffect(() => {
    setIsDark(applyTheme(theme));
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* not fatal */ }
  }, [theme]);

  // Only follow the OS while the person has actually chosen to follow it.
  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setIsDark(applyTheme('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next) => setThemeState(next), []);
  const toggle = useCallback(() => setThemeState(isDark ? 'light' : 'dark'), [isDark]);

  const value = useMemo(() => ({ theme, isDark, setTheme, toggle }), [theme, isDark, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  // Rendered outside the provider (a standalone page, a test): degrade to
  // light rather than crashing the tree.
  return { theme: 'light', isDark: false, setTheme: () => {}, toggle: () => {} };
}
